import { clsx } from 'clsx';
import { useMeta, useLookups } from '../../hooks/useMeta';
import { useNow } from '../../hooks/useNow';
import { DEFAULT_CURRENCY, EMPTY, formatDate, formatDateTime, formatMoney, formatRelative, type RelativeOptions } from '../../lib/format';
import { Avatar, type AvatarSize } from '../ui/Avatar';

export type OwnerAvatarProps = {
  userId: string | null | undefined;
  /** Show the name next to the avatar (default true). */
  showName?: boolean;
  size?: AvatarSize;
  className?: string;
};

/** Lead/task owner from meta users; `null` renders "Unassigned". Inactive users are marked. */
export function OwnerAvatar({ userId, showName = true, size = 'xs', className }: OwnerAvatarProps) {
  const { lookups } = useLookups();
  const user = lookups.userById(userId);
  if (!user) {
    return (
      <span className={clsx('inline-flex items-center gap-1.5 text-slate-400', className)} title="Unassigned">
        <span aria-hidden className={clsx('inline-block shrink-0 rounded-full border border-dashed border-slate-300', size === 'xs' ? 'size-5' : 'size-6')} />
        {showName ? <span className="text-[13px]">Unassigned</span> : <span className="sr-only">Unassigned</span>}
      </span>
    );
  }
  return (
    <span className={clsx('inline-flex min-w-0 items-center gap-1.5', className)} title={user.isActive ? user.name : `${user.name} (inactive)`}>
      <Avatar name={user.name} size={size} className={user.isActive ? undefined : 'opacity-50'} />
      {showName ? (
        <span className={clsx('truncate text-[13px]', user.isActive ? 'text-slate-700' : 'text-slate-400 line-through')}>{user.name}</span>
      ) : (
        <span className="sr-only">{user.name}</span>
      )}
    </span>
  );
}

export type MoneyTextProps = {
  amount: number | null | undefined;
  /** Defaults to the company's default currency from meta (CAD). */
  currency?: string | null;
  compact?: boolean;
  className?: string;
};

/** Currency amount in tabular figures; compact amounts show the exact value on hover. */
export function MoneyText({ amount, currency, compact, className }: MoneyTextProps) {
  const { data: meta } = useMeta();
  const code = currency ?? meta?.settings.defaultCurrency ?? DEFAULT_CURRENCY;
  const locale = meta?.settings.locale;
  if (amount === null || amount === undefined) return <span className={clsx('text-slate-400', className)}>{EMPTY}</span>;
  return (
    <span className={clsx('tabular', className)} title={compact ? formatMoney(amount, code, { locale }) : undefined}>
      {formatMoney(amount, code, { compact, locale })}
    </span>
  );
}

export type RelativeTimeProps = {
  value: string | null | undefined;
  /** 'day' for date-only values (due dates) → "today", "in 3 days". */
  granularity?: RelativeOptions['granularity'];
  className?: string;
};

/** "3 days ago" with the absolute date/time as the tooltip. */
export function RelativeTime({ value, granularity = 'time', className }: RelativeTimeProps) {
  const now = useNow();
  if (!value) return <span className={clsx('text-slate-400', className)}>{EMPTY}</span>;
  return (
    <time
      dateTime={value}
      title={granularity === 'day' ? formatDate(value) : formatDateTime(value)}
      className={clsx('whitespace-nowrap', className)}
    >
      {formatRelative(value, now, { granularity })}
    </time>
  );
}
